/**
 * Schema resolver utility for autocomplete providers.
 *
 * Handles:
 * - Dot notation navigation (user.address.city)
 * - Wildcard matching (user.*, items[*].name)
 * - Block context resolution
 * - Converting schema definitions to AutocompleteItems
 */

import type { BlockContextItem, AutocompleteItem } from '../types';
import type {
  AutocompleteSchema,
  VariableDefinition,
  VariableType,
  BlockDefinition,
  HelperDefinition,
  ResolvedContext,
  ResolvedVariable,
  ResolvedBlock,
} from '../types/schema';

/**
 * Resolves autocomplete options from a schema based on query and block context.
 */
export class SchemaResolver {
  private schema: AutocompleteSchema;
  private variableIndex: Map<string, VariableDefinition>;

  constructor(schema: AutocompleteSchema) {
    this.schema = schema;
    this.variableIndex = new Map();
    this.indexVariables(schema.variables, '');
  }

  /**
   * Build an index of variables by their full path for fast lookups
   */
  private indexVariables(variables: VariableDefinition[], prefix: string): void {
    for (const variable of variables) {
      const path = prefix ? `${prefix}.${variable.name}` : variable.name;
      this.variableIndex.set(path, variable);

      if (variable.type === 'object' && variable.properties) {
        this.indexVariables(variable.properties, path);
      }
    }
  }

  /**
   * Main resolution method: returns available options based on query and context
   */
  resolve(query: string, blockContext: BlockContextItem[]): ResolvedContext {
    // Get the base context based on block nesting
    const baseContext = this.getContextForBlocks(blockContext);

    // Handle dot navigation
    if (query.includes('.')) {
      return this.resolveWithNavigation(query, baseContext, blockContext);
    }

    return baseContext;
  }

  /**
   * Resolve context when navigating into nested properties
   */
  private resolveWithNavigation(
    query: string,
    baseContext: ResolvedContext,
    blockContext: BlockContextItem[]
  ): ResolvedContext {
    const segments = query.split('.');
    const pathPrefix = segments.slice(0, -1).join('.');

    // Try to find the variable at the path prefix
    const parentVariable = this.findVariableByPath(
      pathPrefix,
      baseContext.variables,
      blockContext
    );

    if (!parentVariable) {
      // Path not found - return empty context
      return {
        variables: [],
        blocks: [],
        helpers: baseContext.helpers,
        pathPrefix,
      };
    }

    const resolved: ResolvedVariable[] = [];

    // Handle object navigation
    if (parentVariable.type === 'object' && parentVariable.properties) {
      for (const prop of parentVariable.properties) {
        resolved.push({
          path: `${pathPrefix}.${prop.name}`,
          definition: prop,
        });
      }
    }

    // Handle array navigation - show itemType properties
    if (parentVariable.type === 'array' && parentVariable.itemType) {
      const itemType = parentVariable.itemType;
      if (itemType.type === 'object' && itemType.properties) {
        for (const prop of itemType.properties) {
          resolved.push({
            path: `${pathPrefix}[*].${prop.name}`,
            definition: prop,
          });
        }
      }
    }

    // Handle wildcard support
    if (parentVariable.allowWildcard) {
      resolved.push({
        path: `${pathPrefix}.*`,
        definition: {
          name: '*',
          type: 'any',
          description: 'Match any property',
        },
        isWildcard: true,
      });
    }

    return {
      variables: resolved,
      blocks: [], // Blocks aren't available during path navigation
      helpers: baseContext.helpers,
      pathPrefix,
    };
  }

  /**
   * Find a variable definition by its full path
   */
  private findVariableByPath(
    path: string,
    contextVariables: ResolvedVariable[],
    blockContext: BlockContextItem[]
  ): VariableDefinition | null {
    // First check in the current context
    const contextMatch = contextVariables.find((v) => v.path === path);
    if (contextMatch) {
      return contextMatch.definition;
    }

    // Check in the variable index (full schema)
    const indexed = this.variableIndex.get(path);
    if (indexed) {
      return indexed;
    }

    // Handle iterator context - when inside #each, check if path matches item properties
    if (blockContext.length > 0) {
      const innermostBlock = blockContext[blockContext.length - 1];
      const blockDef = this.findBlockDefinition(innermostBlock.type);

      if (blockDef?.contextBehavior.type === 'iterator' && innermostBlock.variable) {
        // Find the array being iterated
        const arrayVar = this.variableIndex.get(innermostBlock.variable);
        if (arrayVar?.type === 'array' && arrayVar.itemType) {
          // Check if path matches item properties
          const itemType = arrayVar.itemType;
          if (itemType.type === 'object' && itemType.properties) {
            const prop = itemType.properties.find((p) => p.name === path);
            if (prop) {
              return prop;
            }
            // Handle nested paths within item
            const segments = path.split('.');
            let current: VariableDefinition | undefined = itemType;
            for (const segment of segments) {
              if (current?.type === 'object' && current.properties) {
                current = current.properties.find((p) => p.name === segment);
              } else {
                current = undefined;
                break;
              }
            }
            if (current) {
              return current;
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Get the resolved context based on block nesting
   */
  private getContextForBlocks(blockContext: BlockContextItem[]): ResolvedContext {
    if (blockContext.length === 0) {
      return this.getRootContext();
    }

    // Start with root context
    let currentVariables = this.schema.variables;
    let currentBlocks = this.schema.blocks;
    let exposedFromBlock: VariableDefinition[] | undefined;
    let exposedBlocksFromBlock: BlockDefinition[] | undefined;

    // Walk through each block in the context
    for (const block of blockContext) {
      const blockDef = this.findBlockDefinition(block.type, currentBlocks)
        ?? this.findBlockDefinition(block.type, this.schema.blocks);

      if (!blockDef) {
        // Unknown block type - keep current context
        continue;
      }

      switch (blockDef.contextBehavior.type) {
        case 'inherit':
          // Keep the same variables and blocks
          break;

        case 'iterator': {
          // Find the array being iterated and expose its item properties
          const arrayVar = this.findVariableInList(block.variable, currentVariables);
          if (arrayVar?.type === 'array' && arrayVar.itemType) {
            const itemType = arrayVar.itemType;
            if (itemType.type === 'object' && itemType.properties) {
              exposedFromBlock = itemType.properties;
            }
            // Also add special iterator variables
            const behavior = blockDef.contextBehavior;
            if (behavior.specialVariables) {
              const specialVars: VariableDefinition[] = behavior.specialVariables.map(
                (sv) => ({
                  name: sv.name,
                  type: sv.type,
                  description: sv.description,
                })
              );
              exposedFromBlock = [...(exposedFromBlock || []), ...specialVars];
            }
          }
          break;
        }

        case 'scope': {
          // Change context to the variable's properties
          const scopeVar = this.findVariableInList(block.variable, currentVariables);
          if (scopeVar?.type === 'object' && scopeVar.properties) {
            exposedFromBlock = scopeVar.properties;
          }
          break;
        }

        case 'custom': {
          // Replace with block's exposed variables
          if (blockDef.exposedVariables) {
            if (blockDef.contextBehavior.includeParentContext) {
              exposedFromBlock = [...currentVariables, ...blockDef.exposedVariables];
            } else {
              exposedFromBlock = blockDef.exposedVariables;
            }
          }
          // Apply same includeParentContext logic to blocks
          if (blockDef.exposedBlocks) {
            if (blockDef.contextBehavior.includeParentContext) {
              exposedBlocksFromBlock = [...currentBlocks, ...blockDef.exposedBlocks];
            } else {
              exposedBlocksFromBlock = blockDef.exposedBlocks;
            }
          } else if (!blockDef.contextBehavior.includeParentContext) {
            exposedBlocksFromBlock = [];
          }
          break;
        }
      }

      // Update current variables if block exposed new ones
      if (exposedFromBlock !== undefined) {
        currentVariables = exposedFromBlock;
        exposedFromBlock = undefined;
      }

      // Update current blocks if block exposed new ones
      if (exposedBlocksFromBlock !== undefined) {
        currentBlocks = exposedBlocksFromBlock;
        exposedBlocksFromBlock = undefined;
      }
    }

    // Always include global blocks from root schema
    for (const gb of this.schema.blocks) {
      if (gb.global && !currentBlocks.some(b => b.type === gb.type)) {
        currentBlocks = [...currentBlocks, gb];
      }
    }

    // Convert to resolved format
    const variables: ResolvedVariable[] = currentVariables.map((v) => ({
      path: v.name,
      definition: v,
    }));

    // Filter blocks by validParentBlocks
    const innermostBlockType =
      blockContext.length > 0 ? blockContext[blockContext.length - 1].type : null;

    const blocks: ResolvedBlock[] = currentBlocks
      .filter((b) => {
        if (!b.validParentBlocks) return true;
        return innermostBlockType && b.validParentBlocks.includes(innermostBlockType);
      })
      .map((b) => ({
        type: b.type,
        definition: b,
      }));

    return {
      variables,
      blocks,
      helpers: this.schema.helpers,
    };
  }

  /**
   * Get root-level context
   */
  private getRootContext(): ResolvedContext {
    const variables: ResolvedVariable[] = this.schema.variables.map((v) => ({
      path: v.name,
      definition: v,
    }));

    // Filter blocks that don't require specific parents
    const blocks: ResolvedBlock[] = this.schema.blocks
      .filter((b) => !b.validParentBlocks)
      .map((b) => ({
        type: b.type,
        definition: b,
      }));

    return {
      variables,
      blocks,
      helpers: this.schema.helpers,
    };
  }

  /**
   * Find a block definition by type
   */
  findBlockDefinition(
    type: string,
    blocks: BlockDefinition[] = this.schema.blocks
  ): BlockDefinition | null {
    // Check direct matches
    const direct = blocks.find((b) => b.type === type);
    if (direct) return direct;

    // Check nested exposed blocks
    for (const block of blocks) {
      if (block.exposedBlocks) {
        const nested = this.findBlockDefinition(type, block.exposedBlocks);
        if (nested) return nested;
      }
    }

    return null;
  }

  /**
   * Find a helper definition by name
   */
  findHelperDefinition(name: string): HelperDefinition | null {
    return this.schema.helpers?.find((h) => h.name === name) || null;
  }

  /**
   * Get variables that can be used as arguments for a specific helper parameter
   */
  getValidHelperArguments(
    helperName: string,
    paramIndex: number,
    context: ResolvedContext
  ): AutocompleteItem[] {
    const helper = this.findHelperDefinition(helperName);
    if (!helper || paramIndex >= helper.parameters.length) {
      return [];
    }

    const param = helper.parameters[paramIndex];
    const validTypes = Array.isArray(param.type) ? param.type : [param.type];
    const items: AutocompleteItem[] = [];

    for (const variable of context.variables) {
      const def = variable.definition;

      // Filter by valid argument types (skip if 'any' which matches everything)
      if (!validTypes.includes('any' as VariableType) && !validTypes.includes(def.type)) {
        continue;
      }

      // Skip namespaces
      if (def.isNamespace) {
        continue;
      }

      items.push({
        id: `helper-arg-${variable.path}`,
        label: variable.path,
        value: variable.path,
        description: def.description,
        metadata: {
          type: def.type,
        },
      });
    }

    return items;
  }

  /**
   * Find a variable in a list by name or path
   */
  private findVariableInList(
    nameOrPath: string,
    variables: VariableDefinition[]
  ): VariableDefinition | null {
    // Direct match
    const direct = variables.find((v) => v.name === nameOrPath);
    if (direct) return direct;

    // Path match (e.g., "order.items")
    const segments = nameOrPath.split('.');
    let current: VariableDefinition | undefined = variables.find(
      (v) => v.name === segments[0]
    );

    for (let i = 1; i < segments.length && current; i++) {
      if (current.type === 'object' && current.properties) {
        current = current.properties.find((p) => p.name === segments[i]);
      } else {
        current = undefined;
      }
    }

    return current || null;
  }

  /**
   * Convert resolved context to AutocompleteItems for display
   */
  toAutocompleteItems(
    context: ResolvedContext,
    query: string
  ): AutocompleteItem[] {
    const items: AutocompleteItem[] = [];
    const lowerQuery = query.toLowerCase();
    const isBlockQuery = query.startsWith('#');

    // Add matching variables (skip if querying for blocks)
    if (!isBlockQuery) {
      for (const variable of context.variables) {
        const def = variable.definition;

        // Skip namespaces unless there's a trailing dot
        if (def.isNamespace && !query.endsWith('.')) {
          continue;
        }

        // Skip arrays (should be used via blocks, not as plain variables)
        if (def.type === 'array') {
          continue;
        }

        // Filter by query
        if (lowerQuery && !variable.path.toLowerCase().includes(lowerQuery)) {
          continue;
        }

        items.push({
          id: `var-${variable.path}`,
          label: variable.path,
          value: variable.path,
          description: def.description,
          metadata: {
            type: def.type,
            isWildcard: variable.isWildcard,
            example: def.example,
          },
        });
      }
    }

    // Add matching blocks
    const blockQuery = isBlockQuery ? query.slice(1).toLowerCase() : '';
    for (const block of context.blocks) {
      const def = block.definition;

      // Filter by query
      if (isBlockQuery && !def.type.toLowerCase().startsWith(blockQuery)) {
        continue;
      }
      if (!isBlockQuery && lowerQuery && !def.type.toLowerCase().includes(lowerQuery)) {
        continue;
      }

      items.push({
        id: `block-${def.type}`,
        label: `#${def.type}`,
        value: '',
        description: def.description,
        isBlockType: true,
        blockType: def.type,
        metadata: {
          argumentType: def.argumentType,
          validArgumentTypes: def.validArgumentTypes,
          example: def.example,
        },
      });
    }

    // Add helpers (if not filtering for blocks)
    if (!isBlockQuery && context.helpers) {
      for (const helper of context.helpers) {
        if (lowerQuery && !helper.name.toLowerCase().includes(lowerQuery)) {
          continue;
        }

        items.push({
          id: `helper-${helper.name}`,
          label: helper.name,
          value: helper.name,
          description: helper.description,
          metadata: {
            isHelper: true,
            parameters: helper.parameters,
            returnType: helper.returnType,
            example: helper.example,
          },
        });
      }
    }

    return items;
  }

  /**
   * Get variables that can be used as arguments for a specific block type
   */
  getValidBlockArguments(
    blockType: string,
    context: ResolvedContext
  ): AutocompleteItem[] {
    const blockDef = this.findBlockDefinition(blockType);
    if (!blockDef || blockDef.argumentType !== 'variable') {
      return [];
    }

    const validTypes = blockDef.validArgumentTypes;
    const items: AutocompleteItem[] = [];

    for (const variable of context.variables) {
      const def = variable.definition;

      // Filter by valid argument types if specified
      if (validTypes && !validTypes.includes(def.type)) {
        continue;
      }

      // Skip namespaces
      if (def.isNamespace) {
        continue;
      }

      items.push({
        id: `arg-${variable.path}`,
        label: variable.path,
        value: variable.path,
        description: def.description,
        metadata: {
          type: def.type,
        },
      });
    }

    return items;
  }

  /**
   * Validate that a variable path exists and return its definition
   */
  validatePath(
    path: string,
    blockContext: BlockContextItem[]
  ): { valid: boolean; definition?: VariableDefinition; error?: string } {
    const context = this.getContextForBlocks(blockContext);
    const def = this.findVariableByPath(path, context.variables, blockContext);

    if (!def) {
      return {
        valid: false,
        error: `Variable "${path}" is not defined`,
      };
    }

    if (def.isNamespace) {
      return {
        valid: false,
        error: `"${path}" is a namespace, not a value. Try "${path}.<property>"`,
      };
    }

    return {
      valid: true,
      definition: def,
    };
  }
}

/**
 * Helper function to flatten variables into a list of paths
 */
export function flattenVariables(
  variables: VariableDefinition[],
  prefix: string = ''
): Array<{ path: string; definition: VariableDefinition }> {
  const result: Array<{ path: string; definition: VariableDefinition }> = [];

  for (const variable of variables) {
    const path = prefix ? `${prefix}.${variable.name}` : variable.name;

    result.push({ path, definition: variable });

    if (variable.type === 'object' && variable.properties) {
      result.push(...flattenVariables(variable.properties, path));
    }

    if (variable.type === 'array' && variable.itemType) {
      const itemPath = `${path}[*]`;
      result.push({ path: itemPath, definition: variable.itemType });

      if (
        variable.itemType.type === 'object' &&
        variable.itemType.properties
      ) {
        result.push(...flattenVariables(variable.itemType.properties, itemPath));
      }
    }
  }

  return result;
}
